// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Group, Space, Stack, Switch, Tabs, Text } from '@mantine/core';
import { Dispatch, FC, useContext, useEffect, useMemo, useState } from 'react';
import { i18n } from '@lingui/core';
import { useListState, UseListStateHandlers } from '@mantine/hooks';
import { DndProvider } from 'react-dnd-latest';
import { HTML5Backend } from 'react-dnd-html5-backend-latest';
import { IconEdit, IconTrash } from '@tabler/icons';
import PanePaper from '../../../Molecules/PanePaper';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import { NotificationConfig, WorkflowTemplateListViewDTO } from '../../../Types/LogT';
import { WorkflowS } from '../../../Service/WorkflowS';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import EmptyText from '../../../Atoms/EmptyText';
import ConfigOption from '../../../Atoms/ConfigOption';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import { NotificationsContext } from '../../../Service/Context/NotificationsContext';
import { LogS } from '../../../Service/LogS';
import BookitupTimeInput from '../../../Atoms/inputs/BookitupTimeInput';
import { GrayBoxV2 } from '../../../Atoms/GrayBox';
import WorkflowTemplateListItem from './WorkflowTemplateListItem';
import { openCreateTemplateModal, openRenameGroupModal } from './WorkflowTemplateModals';

const WorkflowTemplates = () => {
  const [templates, handlers] = useListState<WorkflowTemplateListViewDTO>([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { config, updateConfig } = useContext(NotificationsContext);
  const { setState: setTemplates, append } = handlers;
  const [activeTab, setActiveTab] = useState<string | null>('Allgemein');
  const [configLoading, setConfigLoading] = useState(false);
  const { sendMailFromHour, sendMailToHour } = config;
  const sendMailTimeFrameEnabled = !!(sendMailFromHour || sendMailToHour);
  const groups = useMemo(
    () =>
      Array.from(new Set(templates.map((t) => t.groupName ?? 'Allgemein'))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [templates],
  );

  useEffect(() => {
    setLoading(true);
    WorkflowS.fetchTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  }, [setTemplates]);

  const createTemplate = () => {
    const existingNames = templates.map((template) => template.name);
    executeWithLicenceCheck(LicenceS.Restrictions.AUTOMATIONS, () =>
      openCreateTemplateModal({ onCreate: append, existingNames, existingCategories: groups }),
    );
  };

  const editNotificationConfig = async (patch: Partial<NotificationConfig>) => {
    setConfigLoading(true);
    const cfg = await LogS.patchNotificationConfig(patch);
    if (cfg) {
      updateConfig(cfg);
      setConfigLoading(false);
      return true;
    }
    setConfigLoading(false);
    return false;
  };

  const toggleSendMailTimeFrame = () => {
    if (sendMailTimeFrameEnabled) {
      editNotificationConfig({ sendMailFromHour: null, sendMailToHour: null });
      return;
    }
    editNotificationConfig({
      sendMailFromHour: sendMailFromHour ?? '10:00',
      sendMailToHour: sendMailToHour ?? '20:00',
    });
  };

  return (
    <PanePaper
      title="Workflow-Vorlagen"
      tooltip="help.settings.workflow.pane"
      rightSection={<BookitupAddIcon onClick={createTemplate} />}
    >
      <Text>{i18n._('workflow.templates.description')}</Text>
      <Space h="xl" />
      {loading && <SkeletonItems height={50} />}
      {!loading && templates.length === 0 && (
        <Text italic size="xs" color="dimmed" mb="xl">
          {i18n._('workflow.templates.add')}
        </Text>
      )}{' '}
      <GrayBoxV2>
        <ConfigOption
          title="Archiviert angezeigt"
          description="Archivierte Workflow-Vorlagen anzeigen"
          rightSection={<Switch checked={showArchived} onChange={() => setShowArchived(!showArchived)} />}
        />
        <Space h="sm" />
        {/*<ConfigOption*/}
        {/*  title="Versandzeitraum festlegen"*/}
        {/*  description="Lege fest, in welchem Zeitraum geplante Kunden-E-Mails aus Workflows versendet werden sollen. So gehen automatische Nachrichten nur tagsueber zu passenden Uhrzeiten raus."*/}
        {/*  rightSection={*/}
        {/*    <Switch disabled={configLoading} checked={sendMailTimeFrameEnabled} onChange={toggleSendMailTimeFrame} />*/}
        {/*  }*/}
        {/*/>*/}
        {sendMailTimeFrameEnabled && (
          <>
            <Space h="xs" />
            <Group spacing="xs" grow align="flex-start">
              <BookitupTimeInput
                label="Von"
                initialValue={sendMailFromHour}
                patchCallback={async (val) => editNotificationConfig({ sendMailFromHour: val })}
                disabled={configLoading}
              />
              <BookitupTimeInput
                label="Bis"
                initialValue={sendMailToHour}
                patchCallback={async (val) => editNotificationConfig({ sendMailToHour: val })}
                disabled={configLoading}
              />
            </Group>
          </>
        )}
      </GrayBoxV2>
      <Space h="md" />
      <DndProvider backend={HTML5Backend}>
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            {groups.map((groupName) => (
              <Tabs.Tab key={groupName} value={groupName}>
                {groupName}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {groups.map((groupName) => (
            <Tabs.Panel key={groupName} value={groupName} pt="xl">
              <GroupTemplates
                templates={templates}
                groupName={groupName === 'Allgemein' ? null : groupName}
                handlers={handlers}
                showArchived={showArchived}
                groups={groups}
                setTab={setActiveTab}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </DndProvider>
    </PanePaper>
  );
};

interface Props {
  templates: WorkflowTemplateListViewDTO[];
  groupName: string | null;
  handlers: UseListStateHandlers<WorkflowTemplateListViewDTO>;
  showArchived: boolean;
  groups: string[];
  setTab: Dispatch<string>;
}

const GroupTemplates: FC<Props> = ({ templates, groupName, handlers, showArchived, groups, setTab }) => {
  const filtered = templates.filter((t) => t.groupName === groupName);
  const { applyWhere } = handlers;

  const rename = (name: string | null) => {
    applyWhere(
      (t) => t.groupName === groupName,
      (t) => ({ ...t, groupName: name }),
    );
    setTab(name === null ? 'Allgemein' : name);
  };

  const deleteGroup = () => {
    if (groupName) {
      const onConfirm = async () => {
        const deleted = await WorkflowS.renameGroup(groupName, null);
        if (deleted) {
          rename(null);
        }
        return deleted;
      };

      openDeleteConfirmModal({
        title: 'Gruppe löschen?',
        description: 'Möchtest Du die Gruppe wirklich löschen?',
        onConfirm,
      });
    }
  };

  return (
    <>
      {filtered.length === 0 && <EmptyText />}
      <Stack>
        {filtered.map((template) => (
          <WorkflowTemplateListItem
            key={template.id}
            template={template}
            handlers={handlers}
            showArchived={showArchived}
            groups={groups}
            setTab={setTab}
          />
        ))}
      </Stack>
      {groupName && (
        <Group position="center" mt="xl">
          <Button
            size="xs"
            variant="subtle"
            leftIcon={<IconEdit size={16} />}
            onClick={() =>
              openRenameGroupModal({ initialValue: groupName, existingCategories: groups, onSuccess: rename })
            }
          >
            Gruppe bearbeiten
          </Button>
          <Button size="xs" color="red" variant="subtle" leftIcon={<IconTrash size={16} />} onClick={deleteGroup}>
            {i18n._('actions.delete')}
          </Button>
        </Group>
      )}
    </>
  );
};

export default WorkflowTemplates;
